import React, { useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom'
import useAuthStore from '../../store/authStore.js'
import { useSettingsStore } from '../../store/settingsStore.js'
import { usePolicyStore } from '../../store/usePolicyStore.js'
import { useLanguage } from '../../i18n/index.jsx'
import { cls } from '../../utils/helpers.js'
import { isNavKeyAvailable } from '../../config/featureModules.js'
import useTipsStore from '../../store/tipsStore.js'
import FeedbackButton from './FeedbackButton.jsx'
import EndDemoButton from './EndDemoButton.jsx'
import type { User } from '../../types/domain'
import {
  MAIN_ITEMS, INVENTORY_GROUP, PRODUCTIONS_GROUP,
  NETWORK_GROUP, LIBRARY_GROUP,
  REPORTS_ITEM, PEOPLE_ITEM, ADMIN_GROUP,
  CREW_GROUP, TOURING_GROUP,
} from './navConstants.jsx'
import type { NavItem, NavGroup, NavChild } from './navConstants.jsx'

interface MobileGroupProps {
  group: NavGroup
  t: (key: string) => string
  filterItems: (items: NavChild[]) => NavItem[]
  closeMenu: () => void
}

function MobileGroup({ group, t, filterItems, closeMenu }: MobileGroupProps) {
  const [open, setOpen] = useState(false)
  const children = filterItems(group.children)
  if (!children.length) return null
  return (
    <>
      <button className="mobile-menu-item" onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        style={{ width: '100%', border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left', fontWeight: 600 }}>
        <span>{group.icon}</span>
        <span style={{ flex: 1 }}>{t(group.key) || group.key}</span>
        <span style={{ fontSize: 'var(--text-tiny)' }}>{open ? '▼' : '▶'}</span>
      </button>
      {open && children.map(c => (
        <NavLink key={c.to} to={c.to}
          className={({ isActive }) => cls('mobile-menu-item', isActive && 'nav-active')}
          style={{ paddingLeft: 40 }}
          onClick={closeMenu}>
          <span></span>
          <span style={{ flex: 1 }}>{t(c.key) || c.key}</span>
        </NavLink>
      ))}
    </>
  )
}

interface MobileMenuProps {
  menuOpen: boolean
  setMenuOpen: (open: boolean) => void
  menuRef: React.RefObject<HTMLDivElement | null>
  appName: string | null | undefined
  user: User | null | undefined
}

export default function MobileMenu({ menuOpen, setMenuOpen, menuRef, appName, user }: MobileMenuProps) {
  const { isAdmin, logout, roleLabel } = useAuthStore()
  const { enabledModules, disabledItems, planFeatures, gbEnabled, platformModules, platformDisabledModules } = useSettingsStore()
  const policyModules = usePolicyStore(s => s.modules)
  const { t } = useLanguage()
  const { enabled: tipsEnabled, toggle: toggleTips } = useTipsStore()
  const adminRole = isAdmin()
  const filterCtx = { adminRole, enabledModules, disabledItems, planFeatures, settingsFlags: { gbEnabled }, platformModules, platformDisabledModules, policyModules }
  const filterItems = (items: NavChild[]): NavItem[] => (items.filter(item => !('subHeader' in item) && isNavKeyAvailable(item.key, filterCtx)) as NavItem[])
  const closeMenu = () => setMenuOpen(false)

  // Audit phase 4 — close on Escape so keyboard users have a non-mouse
  // dismiss path. role="dialog" + aria-modal="true" on the overlay sets
  // up the expectation; this honours it.
  useEffect(() => {
    if (!menuOpen) return undefined
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false) }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [menuOpen, setMenuOpen])

  if (!menuOpen) return null

  return (
    <div
      className="mobile-menu-overlay"
      onClick={closeMenu}
      role="dialog"
      aria-modal="true"
      aria-label={t('ariaNavigationMenu')}
      onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => { if (e.key === 'Escape') closeMenu() }}
    >
      <div className="mobile-menu" ref={menuRef} role="presentation" onClick={e => e.stopPropagation()}>
        <div className="mobile-menu-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <img src="/logo.png" alt="Brighella" style={{ width: 22, height: 22, objectFit: 'contain' }} loading="eager" width={22} height={22} />
            <span className="brand-name">{appName || 'Brighella'}</span>
          </div>
          <button onClick={closeMenu} aria-label={t('closeMenu')}>✕</button>
        </div>

        {filterItems(MAIN_ITEMS).map(item => (
          <NavLink key={item.to} to={item.to} end={item.to === '/'}
            className={({ isActive }) => cls('mobile-menu-item', isActive && 'nav-active')}
            onClick={closeMenu}>
            <span>{item.icon}</span>
            <span style={{ flex: 1 }}>{t(item.key) || item.key}</span>
          </NavLink>
        ))}

        <MobileGroup group={CREW_GROUP}        t={t} filterItems={filterItems} closeMenu={closeMenu} />
        <MobileGroup group={TOURING_GROUP}     t={t} filterItems={filterItems} closeMenu={closeMenu} />
        <MobileGroup group={INVENTORY_GROUP}   t={t} filterItems={filterItems} closeMenu={closeMenu} />
        <MobileGroup group={PRODUCTIONS_GROUP} t={t} filterItems={filterItems} closeMenu={closeMenu} />
        <MobileGroup group={NETWORK_GROUP}     t={t} filterItems={filterItems} closeMenu={closeMenu} />
        <MobileGroup group={LIBRARY_GROUP}     t={t} filterItems={filterItems} closeMenu={closeMenu} />

        {adminRole && (
          <>
            <div style={{ height: 1, background: 'var(--border-subtle)', margin: '4px 0' }} />
            {/* Wave 3b — Reports + People as direct hub doors. */}
            {filterItems([REPORTS_ITEM]).map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => cls('mobile-menu-item', isActive && 'nav-active')}
                onClick={closeMenu}
              >
                <span>{item.icon}</span>
                <span style={{ flex: 1 }}>{t(item.key) || item.key}</span>
              </NavLink>
            ))}
            {filterItems([PEOPLE_ITEM]).map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => cls('mobile-menu-item', isActive && 'nav-active')}
                onClick={closeMenu}
              >
                <span>{item.icon}</span>
                <span style={{ flex: 1 }}>{t(item.key) || item.key}</span>
              </NavLink>
            ))}
            <MobileGroup group={ADMIN_GROUP}   t={t} filterItems={filterItems} closeMenu={closeMenu} />
          </>
        )}

        <div className="mobile-menu-actions">
          <FeedbackButton triggerClassName="mobile-action-btn" triggerStyle={{ boxShadow: 'none' }} />
          <button
            className={cls('mobile-action-btn', tipsEnabled && 'mobile-action-btn--active')}
            onClick={toggleTips}
            aria-pressed={tipsEnabled}
            aria-label={tipsEnabled ? t('tipsDisabled') : t('tipsEnabled')}
          >
            💡 {t('tips')}
          </button>
          <EndDemoButton triggerClassName="mobile-action-btn" triggerStyle={{ boxShadow: 'none' }} />
        </div>

        <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border-subtle)' }}>
          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginBottom: 8 }}>
            {user?.username} · {roleLabel?.() || ''}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => { logout(); closeMenu() }}
            style={{ width: '100%', justifyContent: 'center' }}>
            {t('logout')}
          </button>
        </div>
      </div>
    </div>
  )
}
